import { IntegrationFlowMultipleAuthenticationsAreRequiredModel } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import {
	AppIntegrationFlowResultItemModel,
	AppIntegrationLiveInformationResultStatus,
	getLiveInformation,
	AppIntegrationFlowResult,
	LiveInformationRequestModel
} from "client/Api/Workspace/LiveInformation/Integration/integrationLiveInformation";
import { never } from "client_legacy/ndaw/errors";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";

type IntegrationLiveInformationProviderOptions = {
	intervalInSeconds: number;
};

type IntegrationResourceId = number;

type IntegrationInterests = {
	items?: boolean;
};

export type IntegrationTileItemModel = AppIntegrationFlowResultItemModel & {
	uniqueId: Guid;
};

export type IntegrationUpdateModel = {
	status: AppIntegrationLiveInformationResultStatus.Failure;
} | {
	status: AppIntegrationLiveInformationResultStatus.AuthenticationIsRequired;
	model: IntegrationFlowMultipleAuthenticationsAreRequiredModel;
} | {
	status: AppIntegrationLiveInformationResultStatus.Success;
	items: readonly IntegrationTileItemModel[];
};

export interface IntegrationInterestedParty extends InterestedParty<number, IntegrationResourceId> {
	update(updateModel: IntegrationUpdateModel): void;
};

export class IntegrationLiveInformationProvider extends LiveInformationProvider<
	IntegrationLiveInformationProviderOptions,
	number,
	IntegrationResourceId,
	IntegrationInterests,
	IntegrationInterestedParty,
	IntegrationUpdateModel
> {
	constructor(options: IntegrationLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: readonly [IntegrationResourceId, IntegrationInterests][]
	): Promise<readonly [IntegrationResourceId, IntegrationUpdateModel][]> {
		const results: [IntegrationResourceId, IntegrationUpdateModel][] = [];

		const requests: readonly LiveInformationRequestModel[] = entries.map(
			entry => ({
				integrationId: entry[0]
			})
		);

		const responses: readonly AppIntegrationFlowResult[] = await getLiveInformation(requests);

		for (const response of responses) {
			let updateModel: IntegrationUpdateModel;

			switch (response.status) {
				case AppIntegrationLiveInformationResultStatus.Success:
					updateModel = {
						status: AppIntegrationLiveInformationResultStatus.Success,
						items: response.items.map(
							x => ({
								...x,
								uniqueId: newGuid()
							})
						)
					};

					break;
				case AppIntegrationLiveInformationResultStatus.AuthenticationIsRequired:
					updateModel = {
						status: AppIntegrationLiveInformationResultStatus.AuthenticationIsRequired,
						model: response.authenticationModel
					};

					break;
				case AppIntegrationLiveInformationResultStatus.Failure:
					updateModel = {
						status: AppIntegrationLiveInformationResultStatus.Failure
					};
					break;
				default:
					never(response);
			}

			results.push([response.integrationId, updateModel]);
		}

		return results;
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<IntegrationInterests, IntegrationUpdateModel>,
		response: [IntegrationInterests, IntegrationUpdateModel]
	): void {
		const [interests, updateModel] = response;

		if (!interests.items) {
			throw new Error("Unknown interests");
		}

		cacheItem.data = updateModel;
	}

	protected informParty(
		party: IntegrationInterestedParty,
		cacheItem: [IntegrationInterests, IntegrationUpdateModel]
	): void {
		const [interests, updateModel] = cacheItem;

		if (!interests.items) {
			throw new Error("Unknown interests");
		}

		party.update(updateModel);
	}
}
